import { Injectable } from '@angular/core';

// Access introJs from window object
declare global {
  interface Window {
    introJs: any;
  }
}

export interface TourStep {
  element?: string;
  intro: string;
  title?: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
}

export interface TourOptions {
  steps: TourStep[];
  showProgress?: boolean;
  showBullets?: boolean;
  exitOnOverlayClick?: boolean;
  exitOnEsc?: boolean;
  nextLabel?: string;
  prevLabel?: string;
  skipLabel?: string;
  doneLabel?: string;
  scrollToElement?: boolean;
  scrollPadding?: number;
  overlayOpacity?: number;
  disableInteraction?: boolean;
  lockScroll?: boolean; // New option to lock scrolling during tour
}

@Injectable({
  providedIn: 'root'
})
export class TourService {
  private tour: any;
  private maxRetries = 10;
  private retryDelay = 200; // ms
  private scrollHandler: (() => void) | null = null;
  private scrollLocked = false;
  private originalBodyOverflow = '';
  private originalHtmlOverflow = '';

  constructor() {}

  /**
   * Wait for intro.js to be available
   */
  private waitForIntroJs(): Promise<any> {
    return new Promise((resolve, reject) => {
      let retries = 0;
      const checkIntroJs = () => {
        if (typeof window !== 'undefined' && typeof window.introJs !== 'undefined') {
          resolve(window.introJs);
        } else if (retries < this.maxRetries) {
          retries++;
          setTimeout(checkIntroJs, this.retryDelay);
        } else {
          reject(new Error('intro.js failed to load after ' + this.maxRetries + ' retries'));
        }
      };
      checkIntroJs();
    });
  }

  /**
   * Lock page scrolling during tour
   */
  private lockScroll(): void {
    if (this.scrollLocked) return;

    this.scrollLocked = true;
    this.originalBodyOverflow = document.body.style.overflow;
    this.originalHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    // Add class for additional CSS control
    document.body.classList.add('introjs-tour-active');
  }

  /**
   * Unlock page scrolling after tour ends
   */
  private unlockScroll(): void {
    if (!this.scrollLocked) return;

    this.scrollLocked = false;
    document.body.style.overflow = this.originalBodyOverflow;
    document.documentElement.style.overflow = this.originalHtmlOverflow;

    // Remove class
    document.body.classList.remove('introjs-tour-active');
  }

  /**
   * Add scroll listener to refresh tour position (fallback)
   */
  private addScrollListener(): void {
    if (this.scrollHandler) return;

    let scrollTimeout: any;
    this.scrollHandler = () => {
      // Debounce scroll events
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (this.tour) {
          // Refresh intro.js position
          this.tour.refresh();
        }
      }, 50);
    };

    // Listen on window and main scrollable containers
    window.addEventListener('scroll', this.scrollHandler, true);
  }

  /**
   * Remove scroll listener
   */
  private removeScrollListener(): void {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler, true);
      this.scrollHandler = null;
    }
  }

  /**
   * Setup tour event handlers for cleanup
   */
  private setupTourCleanup(): void {
    // Cleanup on tour complete
    this.tour.oncomplete(() => {
      this.cleanupTour();
    });

    // Cleanup on tour exit (skip/close)
    this.tour.onexit(() => {
      this.cleanupTour();
    });
  }

  /**
   * Cleanup tour resources
   */
  private cleanupTour(): void {
    this.unlockScroll();
    this.removeScrollListener();
  }

  /**
   * Check if a DOM element exists and is currently visible
   */
  isElementVisible(selector: string): boolean {
    const el = document.querySelector(selector) as HTMLElement;
    if (!el) return false;
    if (el.hasAttribute('hidden')) return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
    if (el.offsetWidth === 0 && el.offsetHeight === 0) return false;
    return true;
  }

  /**
   * Filter steps to only include those targeting visible elements
   */
  filterVisibleSteps(steps: TourStep[]): TourStep[] {
    return steps.filter(step => {
      if (!step.element) return true;
      return this.isElementVisible(step.element);
    });
  }

  async startTour(options: TourOptions): Promise<void> {
    try {
      const introJs = await this.waitForIntroJs();

      // Filter out steps targeting hidden or non-existent elements
      const visibleSteps = this.filterVisibleSteps(options.steps);
      if (visibleSteps.length === 0) {
        console.warn('Tour skipped: no visible elements found.');
        return;
      }

      this.tour = introJs();

      this.tour.setOptions({
        steps: visibleSteps.map(step => ({
          element: step.element ? document.querySelector(step.element) : undefined,
          intro: step.intro,
          title: step.title || '',
          position: step.position || 'auto'
        })),
        showProgress: options.showProgress ?? true,
        showBullets: options.showBullets ?? true,
        exitOnOverlayClick: options.exitOnOverlayClick ?? false,
        exitOnEsc: options.exitOnEsc ?? true,
        nextLabel: options.nextLabel ?? 'Next &rarr;',
        prevLabel: options.prevLabel ?? '&larr; Back',
        skipLabel: options.skipLabel ?? '<i class="ti ti-x"></i>',
        doneLabel: options.doneLabel ?? 'Done',
        scrollToElement: options.scrollToElement ?? true,
        scrollPadding: options.scrollPadding ?? 80,
        overlayOpacity: options.overlayOpacity ?? 0.7,
        disableInteraction: options.disableInteraction ?? true,
        tooltipClass: 'custom-intro-tooltip',
        highlightClass: 'custom-intro-highlight',
        helperElementPadding: 10,
        showStepNumbers: false,
        keyboardNavigation: true,
        scrollTo: 'tooltip',
        autoPosition: true
      });

      // Setup cleanup handlers
      this.setupTourCleanup();

      // Lock scroll if enabled (default: true)
      if (options.lockScroll !== false) {
        this.lockScroll();
      }

      // Add scroll listener as fallback for edge cases
      this.addScrollListener();

      this.tour.start();
    } catch (error) {
      console.error('Failed to start tour:', error);
      this.cleanupTour();
    }
  }

  async startTourFromElements(): Promise<void> {
    try {
      const introJs = await this.waitForIntroJs();

      this.tour = introJs();

      this.tour.setOptions({
        showProgress: true,
        showBullets: true,
        exitOnOverlayClick: false,
        exitOnEsc: true,
        nextLabel: 'Next &rarr;',
        prevLabel: '&larr; Back',
        skipLabel: '<i class="ti ti-x"></i>',
        doneLabel: 'Done',
        scrollToElement: true,
        scrollPadding: 80,
        overlayOpacity: 0.7,
        disableInteraction: true,
        tooltipClass: 'custom-intro-tooltip',
        highlightClass: 'custom-intro-highlight',
        helperElementPadding: 10,
        showStepNumbers: false,
        keyboardNavigation: true,
        scrollTo: 'tooltip',
        autoPosition: true
      });

      // Setup cleanup handlers
      this.setupTourCleanup();

      // Lock scroll by default
      this.lockScroll();

      // Add scroll listener as fallback
      this.addScrollListener();

      this.tour.start();
    } catch (error) {
      console.error('Failed to start tour from elements:', error);
      this.cleanupTour();
    }
  }

  /**
   * Manually refresh tour position (useful if layout changes)
   */
  refresh(): void {
    if (this.tour) {
      this.tour.refresh();
    }
  }

  exit(): void {
    if (this.tour) {
      this.tour.exit();
    }
    this.cleanupTour();
  }

  goToStep(step: number): void {
    if (this.tour) {
      this.tour.goToStep(step);
    }
  }

  nextStep(): void {
    if (this.tour) {
      this.tour.nextStep();
    }
  }

  previousStep(): void {
    if (this.tour) {
      this.tour.previousStep();
    }
  }

  onComplete(callback: () => void): void {
    if (this.tour) {
      // Wrap callback to ensure cleanup happens first
      const originalCallback = callback;
      this.tour.oncomplete(() => {
        this.cleanupTour();
        originalCallback();
      });
    }
  }

  onExit(callback: () => void): void {
    if (this.tour) {
      // Wrap callback to ensure cleanup happens first
      const originalCallback = callback;
      this.tour.onexit(() => {
        this.cleanupTour();
        originalCallback();
      });
    }
  }

  onBeforeChange(callback: (targetElement: HTMLElement) => void): void {
    if (this.tour) {
      this.tour.onbeforechange(callback);
    }
  }

  onAfterChange(callback: (targetElement: HTMLElement) => void): void {
    if (this.tour) {
      this.tour.onafterchange(callback);
    }
  }

  /**
   * Check if tour is currently active
   */
  isActive(): boolean {
    return this.scrollLocked;
  }
}
